import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  type User,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from './config'
import type { UserProfile, UserSession } from '@fleetops/types'
import { firestorePaths } from '@fleetops/types'

export async function signIn(email: string, password: string): Promise<UserSession> {
  const credential = await signInWithEmailAndPassword(auth, email, password)
  const session = await resolveUserSession(credential.user)
  if (!session) throw new Error('User profile not found. Contact your administrator.')
  return session
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth)
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

export async function resolveUserSession(firebaseUser: User): Promise<UserSession | null> {
  // tenantId is stored as a custom claim set by a Firebase Function on user creation
  const idTokenResult = await firebaseUser.getIdTokenResult()
  const tenantId = idTokenResult.claims['tenantId'] as string | undefined

  if (!tenantId) return null

  const profileRef = doc(db, firestorePaths.tenantUser(tenantId, firebaseUser.uid))
  const profileSnap = await getDoc(profileRef)

  if (!profileSnap.exists()) return null

  const profile = profileSnap.data() as UserProfile

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email ?? '',
    tenantId,
    role: profile.role,
    assignedFleetIds: profile.assignedFleetIds,
  }
}

export function onAuthChange(callback: (session: UserSession | null) => void): () => void {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback(null)
      return
    }
    const session = await resolveUserSession(user)
    callback(session)
  })
}
